import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner, Card, CardBody } from 'reactstrap';
import { Clock, ArrowRight } from 'lucide-react';
import api from '../helpers/api';
import { formatDurationHours } from '../constants/durationUtils';

const STATUS_COLORS = {
  SUBMITTED: '#34d399',
  PENDING: '#f59e0b',
  IN_PROGRESS: '#8b5cf6',
  ON_HOLD: '#ef4444',
  RESOLVED: '#10b981',
};

const STATUS_LABELS = {
  SUBMITTED: 'OPEN',
};

const formatStatus = (status) => STATUS_LABELS[status] || status?.replace('_', ' ');

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const DURATION_STATUSES = [
  { key: 'submittedHours', labelKey: 'shared.open', defaultLabel: 'Open', status: 'SUBMITTED' },
  { key: 'pendingHours', labelKey: 'shared.pending', defaultLabel: 'Pending', status: 'PENDING' },
  { key: 'inProgressHours', labelKey: 'shared.in_progress', defaultLabel: 'In Progress', status: 'IN_PROGRESS' },
  { key: 'onHoldHours', labelKey: 'shared.on_hold', defaultLabel: 'On Hold', status: 'ON_HOLD' },
];

const TimeToCloseCard = ({ durations }) => {
  const { t } = useTranslation();
  if (!durations) return null;

  const isResolved = durations.totalHours != null;

  const segments = DURATION_STATUSES
    .map((s) => ({ ...s, hours: durations[s.key] || 0 }))
    .filter((s) => s.hours > 0);

  const segmentTotal = segments.reduce((sum, s) => sum + s.hours, 0);

  // Nothing to show if no totalHours and no per-status data
  if (!isResolved && segmentTotal === 0) return null;

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        backgroundColor: '#fafafa',
      }}
    >
      {isResolved && (
        <>
          <div className="d-flex align-items-center gap-2 mb-2">
            <Clock size={16} className="text-muted" />
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t('business.components.status_timeline.time_to_close', 'Time to Close')}</span>
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>
            {formatDurationHours(durations.totalHours, { t })}
          </div>
          <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
            {t('business.components.status_timeline.total_elapsed_time', 'Total elapsed time')}
          </div>
        </>
      )}
      {!isResolved && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <Clock size={16} className="text-muted" />
          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t('business.components.status_timeline.time_in_status', 'Time in Status')}</span>
        </div>
      )}

      {/* Horizontal breakdown bar */}
      {segmentTotal > 0 && (
        <div
          style={{
            display: 'flex',
            height: '8px',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '10px',
          }}
        >
          {segments.map((s) => (
            <div
              key={s.key}
              style={{
                width: `${(s.hours / segmentTotal) * 100}%`,
                backgroundColor: STATUS_COLORS[s.status],
                minWidth: '3px',
              }}
            />
          ))}
        </div>
      )}

      {/* Per-status breakdown */}
      <div className="d-flex flex-wrap gap-3">
        {DURATION_STATUSES.map((s) => {
          const hours = durations[s.key];
          if (hours == null) return null;
          return (
            <div key={s.key} className="d-flex align-items-center gap-1">
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: STATUS_COLORS[s.status],
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                {t(s.labelKey, s.defaultLabel)}: <strong style={{ color: '#374151' }}>{formatDurationHours(hours, { t, compact: true })}</strong>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatusTimeline = ({ workOrderId }) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState(null);
  const [durations, setDurations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatStatusText = (status) => {
    if (status === 'SUBMITTED') return t('shared.open', 'OPEN');
    return t(`shared.${status?.toLowerCase()}`, status?.replace('_', ' '));
  };

  useEffect(() => {
    if (!workOrderId) return;

    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`asset-issues/status-history?workOrderId=${workOrderId}`);
        const response = data.response || {};
        setHistory(response.timeline || []);
        setDurations(response.durations || null);
      } catch (err) {
        console.error('Error fetching status history:', err);
        setError(t('business.components.status_timeline.load_error', 'Failed to load status history'));
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [workOrderId]);

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner size="sm" color="primary" />
        <span className="ms-2 text-muted" style={{ fontSize: '0.85rem' }}>{t('business.components.status_timeline.loading', 'Loading status history...')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-muted text-center py-3" style={{ fontSize: '0.85rem' }}>
        {error}
      </div>
    );
  }

  const hasDurations = durations && durations.totalHours != null;
  const hasTimeline = history && history.length > 0;

  if (!hasTimeline && !hasDurations) {
    return (
      <Card className="border shadow-sm">
        <CardBody>
          <div className="d-flex align-items-center mb-3">
            <div className="rounded-circle bg-light d-flex align-items-center justify-content-center me-3"
              style={{ width: '48px', height: '48px' }}>
              <Clock size={24} className="text-muted" />
            </div>
            <div>
              <h5 className="mb-1 fw-bold">{t('shared.time_tracking', 'Time Tracking')}</h5>
              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>{t('business.components.status_timeline.transition_history_subtitle', 'Status transition history for this ticket')}</p>
            </div>
          </div>
          <div className="text-center py-5">
            <div className="rounded-circle bg-light d-flex align-items-center justify-content-center mx-auto mb-3"
              style={{ width: '64px', height: '64px' }}>
              <Clock size={24} className="text-muted" />
            </div>
            <h5 className="text-muted">{t('business.components.status_timeline.no_data', 'No time tracking data')}</h5>
            <p className="text-muted mb-0">{t('business.components.status_timeline.no_data_subtitle', 'Status transitions will appear here as the ticket progresses')}</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="status-timeline">
      <TimeToCloseCard durations={durations} />

      {hasTimeline && history.map((entry, index) => (
        <div key={entry.id} className="d-flex align-items-start mb-3">
          {/* Timeline dot and line */}
          <div className="d-flex flex-column align-items-center me-3" style={{ minWidth: '20px' }}>
            <div
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: STATUS_COLORS[entry.toStatus] || '#6b7280',
                flexShrink: 0,
              }}
            />
            {index < history.length - 1 && (
              <div
                style={{
                  width: '2px',
                  flexGrow: 1,
                  minHeight: '30px',
                  backgroundColor: '#e5e7eb',
                }}
              />
            )}
          </div>

          {/* Content */}
          <div className="flex-grow-1 pb-1">
            <div className="d-flex align-items-center gap-1 flex-wrap">
              {entry.fromStatus && (
                <>
                  <span
                    className="badge"
                    style={{
                      backgroundColor: STATUS_COLORS[entry.fromStatus] || '#6b7280',
                      fontSize: '0.7rem',
                    }}
                  >
                    {formatStatusText(entry.fromStatus)}
                  </span>
                  <ArrowRight size={12} className="text-muted" />
                </>
              )}
              <span
                className="badge"
                style={{
                  backgroundColor: STATUS_COLORS[entry.toStatus] || '#6b7280',
                  fontSize: '0.7rem',
                }}
              >
                {formatStatusText(entry.toStatus)}
              </span>
            </div>
            <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
              {formatDateTime(entry.changedAt)}
              {entry.changedByName && (
                <span> &middot; {entry.changedByName}</span>
              )}
              {!entry.changedByName && !entry.changedBy && (
                <span> &middot; {t('shared.system', 'System')}</span>
              )}
            </div>
            {entry.durationInStatusHours != null && (
              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                  {t('business.components.status_timeline.time_in_status_label', 'Time in status')}: {formatDurationHours(entry.durationInStatusHours, { t, compact: true })}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatusTimeline;
